import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Search, MessageCircle, ArrowRight } from "lucide-react";
import { useSiteContent } from "@/lib/siteContent";

const CATEGORIES = [
  { id: "all", label: "Toutes" },
  { id: "envoyer", label: "Envoyer" },
  { id: "frais", label: "Frais & délais" },
  { id: "securite", label: "Sécurité" },
  { id: "compte", label: "Compte" },
];

const FAQS = [
  {
    cat: "envoyer",
    q: "Comment envoyer de l'argent avec Swiftlink-Africa ?",
    a: "Cliquez sur « Envoyer maintenant », choisissez votre corridor (Togo → Gabon, Gabon → France, Gabon → Rwanda ou France → Gabon), renseignez les informations du bénéficiaire et le montant, puis effectuez le dépôt sur le moyen de paiement affiché. Notre équipe vérifie le paiement et envoie l'argent au bénéficiaire sous 1 heure.",
  },
  {
    cat: "envoyer",
    q: "Dois-je créer un compte pour envoyer ?",
    a: "Non, l'inscription est facultative. Vous pouvez créer une transaction en tant qu'invité — un identifiant unique (SL-XXXXXXXX) vous permet de suivre votre envoi. Créer un compte vous permet de retrouver l'historique de tous vos envois en un seul endroit.",
  },
  {
    cat: "envoyer",
    q: "Quelles méthodes de paiement sont acceptées ?",
    a: "Pour les dépôts : Moov Money et TMoney au Togo (corridor TG→GA), Airtel Money au Gabon (corridor GA→FR et GA→RW). Pour la réception : Airtel Money / Moov Money au Gabon, PayPal / Wero / Virement instantané SEPA en France, ou MTN Money / Tigo Cash / Airtel Money au Rwanda.",
  },
  {
    cat: "envoyer",
    q: "Quels corridors sont disponibles ?",
    a: "Trois corridors actifs : 🇹🇬 Togo → 🇬🇦 Gabon, 🇬🇦 Gabon → 🇫🇷 France, et 🇬🇦 Gabon → 🇷🇼 Rwanda. D'autres corridors seront bientôt ouverts (suivez nos actualités).",
  },
  {
    cat: "frais",
    q: "Quels sont les frais de transfert ?",
    a: "Corridor Togo → Gabon : variable (voir calculateur en ligne). Corridor Gabon → France : 15 % de frais (réduction de 15 % avec code de parrainage à partir de 200 000 FCFA). Corridor Gabon → Rwanda : 12,5 % (réduction de 10 % avec code de parrainage à partir de 100 000 FCFA). Corridor France → Gabon : 0 % de frais via PayPal Friends & Family (taux fixe 1 € = 656 FCFA, minimum 20 €). Tous les frais sont affichés clairement avant validation grâce à notre calculateur en ligne.",
  },
  {
    cat: "frais",
    q: "Combien de temps prend un transfert ?",
    a: "La majorité des transferts sont traités en moins d'une heure. Le délai dépend du temps de vérification manuelle du dépôt et de la disponibilité du bénéficiaire. Vous êtes notifié dès que le statut change (Vérifié → Envoyé → Terminé).",
  },
  {
    cat: "frais",
    q: "Y a-t-il un montant minimum ou maximum ?",
    a: "Le montant minimum est de 2 000 FCFA. Pour les transactions ≥ 300 000 FCFA, une vérification d'identité (KYC) est requise — pièce d'identité (CNI, passeport ou permis de conduire) à téléverser. Pour les très gros montants, contactez notre service client pour un accompagnement personnalisé.",
  },
  {
    cat: "frais",
    q: "Comment fonctionne le code de parrainage ?",
    a: "Chaque utilisateur dispose d'un code unique (visible sur la page « Parrainage », personnalisable). Partagez-le à vos proches : lorsqu'un filleul l'utilise lors d'un transfert Gabon → France, il bénéficie d'une remise de 15 % sur les frais ; pour Gabon → Rwanda, la remise est de 10 %. Vous gagnez 5 € de commission par transfert ≥ 200 000 FCFA (FR) ou ≥ 100 000 FCFA (RW). Le code est utilisable sans limite par tous vos filleuls.",
  },
  {
    cat: "frais",
    q: "Comment fonctionne la conversion XOF → XAF → EUR ?",
    a: "Le taux XOF ↔ XAF est de 1:1 (les deux devises sont alignées sur le franc CFA). Pour la France, le taux XAF → EUR est mis à jour selon le marché (environ 1 EUR ≈ 656 XAF). Pour le Rwanda, le taux XAF → RWF est également mis à jour régulièrement. Le taux exact utilisé est affiché avant validation.",
  },
  {
    cat: "securite",
    q: "Le service est-il sécurisé ?",
    a: "Oui. Chaque transaction reçoit un identifiant unique vérifié manuellement par notre équipe. Aucune transaction n'est exécutée sans validation humaine. Toutes les communications sont chiffrées (HTTPS/TLS). Les justificatifs sont stockés sur un service d'objets sécurisé avec accès restreint par code à 6 chiffres pour les agents.",
  },
  {
    cat: "securite",
    q: "Pourquoi me demande-t-on une pièce d'identité ?",
    a: "Pour toute transaction ≥ 300 000 FCFA, la réglementation contre le blanchiment de capitaux (LCB-FT) impose une vérification d'identité (KYC). Vous devrez téléverser une CNI, un passeport ou un permis de conduire valide. La pièce est consultée uniquement par nos agents autorisés via un canal sécurisé et n'est jamais partagée.",
  },
  {
    cat: "securite",
    q: "Que faire si je me suis trompé de numéro de bénéficiaire ?",
    a: "Si la transaction est encore en statut « En attente », vous pouvez l'annuler directement depuis la page de suivi. Sinon, contactez immédiatement notre service client WhatsApp avec votre référence SL-XXXXXXXX. Une fois la transaction envoyée, l'annulation n'est plus possible.",
  },
  {
    cat: "securite",
    q: "Comment vérifier qu'un dépôt a bien été reçu ?",
    a: "Saisissez votre code de transaction sur la page « Suivi » : vous y verrez le statut en temps réel, la chronologie complète et toutes les actions effectuées par notre équipe. La preuve de paiement au bénéficiaire est aussi visible une fois la transaction terminée.",
  },
  {
    cat: "compte",
    q: "Comment contacter le service client ?",
    a: "Nous sommes disponibles 7j/7 sur WhatsApp :\n• 🇫🇷 France : +33 7 53 72 81 95\n• 🇷🇼 Rwanda : +250 787 195 886\n• 🇹🇬 Togo : +228 99 82 57 04\nVous pouvez aussi utiliser le bouton WhatsApp flottant en bas à droite de chaque page, ou écrire à contact@swiftlink-africa.com.",
  },
  {
    cat: "compte",
    q: "Comment récupérer mon historique d'envois ?",
    a: "Connectez-vous à votre compte via la page « Connexion » puis cliquez sur « Mon compte » puis « Historique des transferts ». Si vous avez envoyé en tant qu'invité, votre appareil garde une trace locale de vos codes (« Mes envois récents »). Pour un suivi cross-device, créez un compte gratuitement.",
  },
];

export default function FAQ() {
  const sc = useSiteContent();
  const [cat, setCat] = useState("all");
  const [query, setQuery] = useState("");

  // Inject Schema.org FAQPage JSON-LD for SEO rich snippets
  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQS.map((f) => ({
        "@type": "Question",
        "name": f.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": f.a,
        },
      })),
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "sl-faq-jsonld";
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    // Also set a descriptive page title + meta description for SEO
    const prevTitle = document.title;
    document.title = "FAQ — Swiftlink-Africa · Transfert d'argent Togo Gabon France";
    const metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute("content");
    metaDesc?.setAttribute("content", "Questions fréquentes sur Swiftlink-Africa : frais (5% Togo→Gabon, 15% Gabon→France), délais (sous 1h), Mobile Money, PayPal, Wero, virement SEPA, sécurité et suivi.");

    return () => {
      script.remove();
      document.title = prevTitle;
      if (metaDesc && prevDesc) metaDesc.setAttribute("content", prevDesc);
    };
  }, []);

  const filtered = FAQS.filter((f) => {
    if (cat !== "all" && f.cat !== cat) return false;
    if (query) {
      const q = query.toLowerCase();
      return f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <div className="min-h-screen sl-hero-bg">
      <Header />
      <div className="pt-28 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[var(--sl-secondary-dark)]" data-testid="faq-header">
            Aide & FAQ
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold mt-3 leading-tight">
            Questions fréquentes.
          </h1>
          <p className="mt-4 text-gray-600 max-w-2xl">
            {sc.faq_intro || "Tout ce que vous voulez savoir sur l'envoi d'argent avec Swiftlink-Africa. Si votre question n'est pas listée, écrivez-nous sur WhatsApp."}
          </p>

          {/* Search */}
          <div className="mt-8 relative">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher une question…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="sl-input pl-11"
              data-testid="faq-search"
            />
          </div>

          {/* Categories */}
          <div className="mt-5 flex gap-2 flex-wrap">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                onClick={() => setCat(c.id)}
                className={`px-4 py-2 rounded-full text-sm border transition ${
                  cat === c.id
                    ? "bg-[var(--sl-primary)] text-white border-[var(--sl-primary)]"
                    : "bg-white text-gray-700 border-gray-200 hover:border-gray-300"
                }`}
                data-testid={`faq-cat-${c.id}`}
              >
                {c.label}
              </button>
            ))}
          </div>

          {/* Accordion */}
          <div className="sl-card mt-8 p-2 sm:p-4" data-testid="faq-list">
            {filtered.length === 0 ? (
              <div className="p-10 text-center text-gray-500">
                Aucune réponse correspondante. Contactez-nous sur WhatsApp pour une réponse personnalisée.
              </div>
            ) : (
              <Accordion type="single" collapsible className="w-full">
                {filtered.map((f, i) => (
                  <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`}>
                    <AccordionTrigger className="text-left font-heading font-semibold text-base hover:no-underline px-3">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600 text-sm leading-relaxed whitespace-pre-line px-3 pb-4">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </div>

          {/* CTA WhatsApp */}
          <div
            className="mt-10 rounded-2xl p-6 sm:p-8 text-white flex items-center gap-4 flex-wrap"
            style={{ background: "linear-gradient(135deg, var(--sl-primary-dark), var(--sl-primary))" }}
          >
            <div className="w-14 h-14 rounded-full grid place-items-center flex-shrink-0" style={{ background: "#25D366" }}>
              <MessageCircle size={26} className="text-white" />
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="font-heading text-xl font-bold">Vous ne trouvez pas la réponse ?</div>
              <div className="text-sm text-white/70 mt-1">
                Notre équipe répond 7j/7 sur WhatsApp.
              </div>
            </div>
            <a
              href="https://wa.me/33753728195"
              target="_blank"
              rel="noreferrer"
              className="sl-btn-secondary"
              data-testid="faq-whatsapp-cta"
            >
              Écrire sur WhatsApp <ArrowRight size={16} />
            </a>
          </div>

          <div className="mt-6 text-center">
            <Link to="/envoyer" className="text-sm text-[var(--sl-primary)] hover:underline">
              Prêt à envoyer ? Commencer un transfert →
            </Link>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
